/**
 * Transforms the grpc from the server to retrieve useful additional information like server side stack traces.
 */
import { BrewManApiError, UnauthenticatedStatusCode } from "@/app/errors";
import userStore from "@/store/modules/userStore";
import eventLogger from "@/app/eventlogging/eventLogger";
import type { ResponseInterceptor } from "@/app/api";

export const transformErrorInterceptor: ResponseInterceptor = (
  err: BrewManApiError | undefined,
  fetchResponse: Response,
  responseData: any,
  context: {
    userFriendlyServiceName: string;
    preAsyncStackTrace: string;
    requestStartTimeMsSinceEpoch: number;
  }
): [BrewManApiError | undefined, any] => {
  let errorObj;
  if (!fetchResponse.ok) {
    errorObj = BrewManApiError.fromBm7ErrorJson(
      fetchResponse.status,
      responseData,
      context.userFriendlyServiceName
    );
    errorObj.stack = context.preAsyncStackTrace;
  } else if (err) {
    errorObj = err;
  }

  if (errorObj) {
    // The pre-call stack is more useful than the current callstack which just
    // contains the async entry point of the return of the API call.
    errorObj.stack = context.preAsyncStackTrace;

    if (errorObj.code === UnauthenticatedStatusCode) {
      // TODO error handling
      userStore.signOut("api return unathenicated").then();
    }

    if (
      errorObj.code === UnauthenticatedStatusCode &&
      errorObj.reasonCode === "NoLicense"
    ) {
      userStore.setHasLicense(false);
    }
  }

  // No error so passthrough
  return [errorObj, responseData];
};

export const transformRequestRttInterceptor: ResponseInterceptor = (
  err: BrewManApiError | undefined,
  fetchResponse: Response,
  responseData: any,
  context: {
    userFriendlyServiceName: string;
    preAsyncStackTrace: string;
    requestStartTimeMsSinceEpoch: number;
    isDbWrite: boolean;
  }
): [BrewManApiError | undefined, any] => {
  const requestRoundTripMs =
    new Date().getTime() - context.requestStartTimeMsSinceEpoch;

  const isRpcResponseError = !!err;

  // Log rpc request RTT
  if (requestRoundTripMs > 1000 || context.isDbWrite || isRpcResponseError)
    eventLogger.logEvent("rpc_request", {
      rpcRequestName: context.userFriendlyServiceName,
      rpcRequestTimeMs: requestRoundTripMs,
      isRpcResponseError,
    });

  // Passthrough
  return [err, responseData];
};
